import xylemLogoUrl from "@assets/xylem_1773086696493.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Link, useLocation } from "wouter";
import {
  Home,
  SlidersHorizontal,
  BarChart3,
  FileText,
  SearchCheck,
  HelpCircle,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Info,
  Database,
  FlaskConical,
  Download,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useProjectContext } from "@/lib/project-context";

const navItems = [
  { title: "Home", url: "/", icon: Home },
  { title: "Control Panel", url: "/field", icon: SlidersHorizontal },
  { title: "Analysis", url: "/analysis", icon: SearchCheck },
  { title: "Run Analysis", url: "/run-analysis", icon: FlaskConical },
  { title: "Results", url: "/results", icon: BarChart3 },
  { title: "Field Summary", url: "/reports", icon: FileText },
  { title: "Local Store", url: "/local-store", icon: Database },
  { title: "Projects", url: "/projects", icon: FolderOpen },
];

function SidebarCollapseButton() {
  const { toggleSidebar, state } = useSidebar();

  return (
    <button
      onClick={toggleSidebar}
      className="absolute -right-3 top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-full border bg-background shadow-sm hover:bg-muted transition-colors"
      data-testid="button-sidebar-collapse"
      aria-label="Toggle Sidebar"
    >
      {state === "expanded" ? (
        <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </button>
  );
}

function ProjectContextSection() {
  const { currentProject } = useProjectContext();
  const { state } = useSidebar();
  const [hasNewData, setHasNewData] = useState(false);

  useEffect(() => {
    if (!currentProject) {
      setHasNewData(false);
      return;
    }
    const interval = setInterval(() => {
      setHasNewData(true);
      const sidebarEl = document.querySelector("[data-sidebar='sidebar']");
      if (sidebarEl) {
        sidebarEl.classList.add("sidebar-flash");
        setTimeout(() => sidebarEl.classList.remove("sidebar-flash"), 600);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [currentProject]);

  if (state === "collapsed") {
    return null;
  }

  if (!currentProject) {
    return (
      <div className="px-2 py-1.5 group-data-[collapsible=icon]:hidden" data-testid="sidebar-project-context">
        <p className="text-xs text-muted-foreground mb-1.5 text-center">No project selected</p>
        <Link href="/projects">
          <span
            className="flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md bg-sidebar-primary text-sidebar-primary-foreground text-xs font-medium hover:bg-sidebar-primary/90 transition-colors cursor-pointer text-center"
            data-testid="link-select-project"
          >
            <FolderOpen className="h-3.5 w-3.5" />
            Select a project
          </span>
        </Link>
      </div>
    );
  }

  const tileBg = hasNewData
    ? "bg-orange-500 hover:bg-orange-600"
    : "bg-sidebar-primary hover:bg-sidebar-primary/90";

  const TileIcon = hasNewData ? Info : FolderOpen;

  return (
    <Link href="/projects">
      <div
        className={`group-data-[collapsible=icon]:hidden rounded-md mx-1 cursor-pointer transition-colors ${tileBg} ${hasNewData ? "project-tile-new-data" : ""}`}
        data-testid="sidebar-project-context"
        title={hasNewData ? "New data added — analysis needed" : undefined}
      >
        <div className="px-2 py-1.5 flex items-center gap-2">
          <TileIcon className="h-4 w-4 text-white/80 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-semibold break-words text-white" data-testid="text-project-name">{currentProject.name}</p>
            <p className="text-[10px] text-white/70 break-words" data-testid="text-project-code">
              {hasNewData ? "New data — needs analysis" : currentProject.pipelineName}
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
}


export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const [generatingHelp, setGeneratingHelp] = useState(false);
  const { setOpen } = useSidebar();

  const openHelpGuide = async () => {
    setGeneratingHelp(true);
    try {
      const { generateHelpGuide } = await import("@/lib/help-guide");
      const dataUrl = generateHelpGuide();
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "Vernier_Help_Guide.pdf";
      link.click();
    } finally {
      setGeneratingHelp(false);
    }
  };

  const handleNavClick = (item: { title: string; url: string }, e: React.MouseEvent) => {
    if (item.url === "/results" || item.url === "/reports") {
      e.preventDefault();
      setLocation(item.url);
      setTimeout(() => {
        const tile = document.querySelector("[data-testid='sidebar-project-context']");
        if (tile) {
          tile.classList.add("sidebar-flash");
          setTimeout(() => tile.classList.remove("sidebar-flash"), 600);
        }
      }, 350);
    }
  };

  const renderNavItem = (item: { title: string; url: string; icon: React.ElementType }) => {
    const isActive =
      item.url === "/"
        ? location === "/"
        : location.startsWith(item.url);
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={item.title}
          data-testid={`nav-${item.title.toLowerCase().replace(/\s/g, "-")}`}
        >
          <Link href={item.url} onClick={(e) => handleNavClick(item, e)}>
            <item.icon className="h-4 w-4" />
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="relative">
      <SidebarHeader className="px-3 py-3 group-data-[collapsible=icon]:p-1.5 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
        <div className="flex items-center gap-2 overflow-hidden group-data-[collapsible=icon]:justify-center">
          <img src={xylemLogoUrl} alt="Xylem" className="h-8 shrink-0 rounded object-contain group-data-[collapsible=icon]:h-7" />
          <span className="text-sm font-semibold text-sidebar-foreground truncate group-data-[collapsible=icon]:hidden">
            Vernier
          </span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map(renderNavItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-2 space-y-1.5">
        <ProjectContextSection />
        <SidebarSeparator />
        <SidebarMenu>
          {import.meta.env.VITE_DESKTOP_DOWNLOAD_URL && (
            <SidebarMenuItem className="flex justify-center">
              <SidebarMenuButton
                tooltip="Download Desktop App"
                asChild
                data-testid="button-download-desktop"
                className="justify-center"
              >
                <a href={import.meta.env.VITE_DESKTOP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4" />
                  <span>Desktop App</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem className="flex justify-center">
            <SidebarMenuButton
              tooltip="Help Guide"
              onClick={openHelpGuide}
              disabled={generatingHelp}
              data-testid="button-help-guide"
              className="justify-center"
            >
              <HelpCircle className="h-4 w-4" />
              <span>{generatingHelp ? "Generating..." : "Help"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarCollapseButton />
    </Sidebar>
  );
}
